/**
 * SA Credits Banner Component
 *
 * Full-width banner for displaying credits status above Smart Asset cards.
 * Shows balance, burn rate, projected depletion, and quick top-up CTA.
 */

import React, { useState } from "react";
import { ASSET_PATHS } from "../../../config";

// Same threshold as SmartAssetCard for consistent UX
const DOUBLE_CLICK_THRESHOLD = 300; // ms

interface CreditsBannerProps {
  balance?: number;
  burnRate?: number;
  projectedDays?: number;
  autoRefillEnabled?: boolean;
  className?: string;
  onTopUp?: () => void;
  /** Callback to navigate to credits page (used for state-based navigation) */
  onNavigateToCredits?: () => void;
}

export const CreditsBanner: React.FC<CreditsBannerProps> = ({
  balance = 847.5,
  burnRate = 12.5,
  projectedDays = 68,
  autoRefillEnabled = false,
  className = "",
  onTopUp,
  onNavigateToCredits,
}) => {
  const [lastClickTime, setLastClickTime] = useState(0);
  const [isSelected, setIsSelected] = useState(false);

  const isLowBalance = balance < 100;
  const isWarning = projectedDays < 14;

  // Double-click to open, single-click to select (consistent with SmartAssetCard)
  const handleBannerClick = (e?: React.MouseEvent | React.KeyboardEvent) => {
    e?.stopPropagation(); // Prevent container deselect handler from interfering
    const now = Date.now();

    if (now - lastClickTime < DOUBLE_CLICK_THRESHOLD) {
      // Double click - open credits page
      onNavigateToCredits?.();
      setLastClickTime(0);
      setIsSelected(false);
    } else {
      // Single click - toggle selection
      setIsSelected(!isSelected);
      setLastClickTime(now);
    }
  };

  const handleTopUp = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onTopUp) {
      onTopUp();
    } else {
      onNavigateToCredits?.();
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={handleBannerClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          // Use same double-click logic for keyboard
          handleBannerClick();
        }
      }}
      data-testid="credits-banner"
      className={`w-full p-4 rounded-lg border cursor-pointer transition-colors ${
        isLowBalance
          ? "bg-sa-danger/10 border-sa-danger/40 hover:bg-sa-danger/10"
          : "bg-gradient-to-r from-primary/10 to-purple-500/10 border-primary/40 hover:from-primary/10 hover:to-purple-500/10"
      } ${isSelected ? "ring-2 ring-primary ring-offset-2" : ""} ${className}`}
    >
      <div className="flex items-center justify-between flex-wrap gap-4">
        {/* Left: Battery Icon + Title + Balance */}
        <div className="flex items-center gap-4">
          {/* Battery icon with fill level based on balance */}
          <div className="relative w-14 h-8 flex-shrink-0">
            {/* Battery body */}
            <div
              className={`w-12 h-8 rounded-md border-2 ${
                isLowBalance ? "border-sa-danger" : "border-primary"
              }`}
            >
              {/* Battery fill - width based on projected days (max 90 days = full) */}
              <div
                className={`h-full rounded-sm transition-all ${
                  isLowBalance
                    ? "bg-red-500"
                    : isWarning
                      ? "bg-yellow-500"
                      : "bg-gradient-to-r from-primary to-purple-500"
                }`}
                style={{ width: `${Math.min((projectedDays / 90) * 100, 100)}%` }}
              />
            </div>
            {/* Battery terminal */}
            <div
              className={`absolute right-0 top-1/2 -translate-y-1/2 w-1.5 h-3 rounded-r-sm ${
                isLowBalance ? "bg-red-500" : "bg-primary"
              }`}
            />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <img src={ASSET_PATHS.SA_BRAIN_LOGO} alt="Smart Assets" className="w-5 h-5" />
              <span className="text-sm font-semibold text-sa-text">SA Credits</span>
              {isLowBalance && (
                <span className="px-2 py-0.5 text-[10px] font-medium bg-sa-danger/10 text-sa-danger rounded-full">
                  Low Balance
                </span>
              )}
              {!isLowBalance && !autoRefillEnabled && (
                <span className="px-2 py-0.5 text-[10px] font-medium bg-sa-warning/10 text-sa-warning rounded-full">
                  No Auto-Refill
                </span>
              )}
            </div>
            <p
              className={`text-2xl font-bold flex items-center ${isLowBalance ? "text-sa-danger" : "text-sa-text"}`}
            >
              {balance.toLocaleString("en-US", {
                minimumFractionDigits: 1,
                maximumFractionDigits: 1,
              })}
              <img src={ASSET_PATHS.PAIR_ICON} alt="PAIR" className="w-5 h-5 ml-1" />
            </p>
          </div>
        </div>

        {/* Center: Stats */}
        <div className="flex items-center gap-6 text-sm">
          <div className="text-center">
            <p className="text-sa-text-muted text-xs">Burn Rate</p>
            <p className="font-semibold text-sa-text flex items-center justify-center">
              {burnRate.toFixed(1)}
              <img src={ASSET_PATHS.PAIR_ICON} alt="PAIR" className="w-3.5 h-3.5 mx-0.5" />
              /day
            </p>
          </div>
          <div className="h-8 w-px bg-sa-border" />
          <div className="text-center">
            <p className="text-sa-text-muted text-xs">Depletes In</p>
            <p className={`font-semibold ${isWarning ? "text-sa-warning" : "text-sa-text"}`}>
              {projectedDays} days
            </p>
          </div>
        </div>

        {/* Right: Top Up Button */}
        <button
          onClick={handleTopUp}
          className="px-6 py-2 bg-purple-500/10 hover:bg-purple-500/10 text-purple-500 text-sm font-medium rounded-lg transition-colors flex-shrink-0"
        >
          Top Up Credits
        </button>
      </div>
    </div>
  );
};
